<script setup lang="ts">
import { onMounted, ref } from "vue";

import FullPageCenteredLayout from "@/layouts/FullPageCenteredLayout.vue";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";
import DispatchViewAuthenticated from "@/views/dispatch/DispatchViewAuthenticated.vue";

const api = useApi();
const isUserAuthenticated = ref(false);

onMounted(async () => {
  isUserAuthenticated.value = await api.isUserAuthenticated();
});
</script>

<template>
  <div class="dispatch">
    <DispatchViewAuthenticated v-if="isUserAuthenticated" />
    <FullPageCenteredLayout v-else>
      <div class="unauthenticated">
        <h1 class="hero">Own <em>Your</em> Data Science</h1>
        <div class="subtitle label-s">
          Skore empowers teams to address root problems early, seamlessly integrating into existing
          ML workflows. Built by the minds behind scikit-learn, skore delivers trusted innovation
          from the source.
        </div>
        <div class="ctas">
          <RouterLink :to="{ name: RouteNames.Login }">
            <div class="icon blue"><i class="icon icon-existing-user" /></div>
            <div class="title label-l">
              I already have an account&nbsp;<i class="icon icon-ascending-arrow" />
            </div>
            <div class="subtitle paragraph-s">Let’s directly connect to your session.</div>
          </RouterLink>
          <a href="https://probabl.ai/skore#join-waitlist">
            <div class="icon orange"><i class="icon icon-new-user" /></div>
            <div class="title label-l">
              Join the waitlist&nbsp;<i class="icon icon-ascending-arrow" />
            </div>
            <div class="subtitle paragraph-s">Be amongst the first to get access to Skore!</div>
          </a>
        </div>
        <div class="links">
          <a href="https://probabl.ai" target="_blank">
            <span class="text">Probabl</span> <i class="icon icon-external-link" />
          </a>
          <a href="https://docs.skore.probabl.ai" target="_blank">
            <span class="text">Documentation</span> <i class="icon icon-external-link" />
          </a>
          <a href="https://github.com/probabl-ai/skore" target="_blank">
            <span class="text">GitHub</span> <i class="icon icon-external-link" />
          </a>
        </div>
      </div>
    </FullPageCenteredLayout>
  </div>
</template>

<style scoped>
.dispatch {
  --ctas-width: 70dvw;

  &:has(.unauthenticated) {
    background-color: var(--color-background-secondary);
  }

  .unauthenticated {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-8);
    text-align: center;

    .hero {
      width: 52vw;
      color: var(--color-text-primary);
      font-family: GeistMono, monospace;
      letter-spacing: -0.3rem;
      line-height: 4.35rem;

      em {
        color: var(--color-text-branding);
        font-style: normal;
      }
    }

    .subtitle {
      width: 38vw;
      color: var(--color-text-secondary);
    }

    .ctas {
      display: flex;
      width: var(--ctas-width);
      flex-direction: row;
      margin: var(--spacing-24) 0;
      gap: var(--spacing-4);

      a {
        display: flex;
        width: calc(50% - var(--spacing-4));
        height: auto;
        flex-direction: column;
        align-items: center;
        padding: var(--spacing-24) var(--spacing-16);
        border-radius: var(--radius-s);
        background-color: var(--color-background-primary);
        gap: var(--spacing-8);
        text-align: center;
        text-decoration: none;

        div.icon {
          display: flex;
          width: 39px;
          height: 39px;
          flex-direction: column;
          justify-content: center;
          border: solid var(--stroke-width-m) var(--color-stroke-background-primary);
          border-radius: var(--radius-s);

          &.orange {
            color: var(--color-text-tertiary);
          }

          &.blue {
            color: var(--color-text-branding);
          }
        }

        .title {
          color: var(--color-text-primary);

          & > i {
            vertical-align: middle;
          }
        }

        .subtitle {
          max-width: 80%;
          color: var(--color-text-secondary);
        }
      }
    }

    .links {
      display: flex;
      flex-direction: row;
      gap: var(--spacing-80);

      a,
      a:visited {
        color: var(--color-text-secondary);
        text-decoration: none;

        .text {
          text-decoration: underline;
          text-underline-offset: var(--spacing-4);
        }

        .icon {
          padding-left: var(--spacing-4);
          vertical-align: middle;
        }
      }
    }
  }
}

@media screen and (width >= 1025px) {
  .dispatch {
    --ctas-width: 55dvw;
  }
}
</style>
